/**
 * Admin Settings page — CASL helpers.
 *
 * Subject already used by this page: `admin`.
 * Page/nav/route: manage OR create on `admin` (or manage all).
 * Role subjects (`read` + `admin` / `read` + `master`) are a legacy fallback
 * only — they are not required when manage/create is granted.
 *
 * Action-level:
 *   create  — add rows / assign DID
 *   manage  — create + edit/save + delete
 *   create-only does not get delete
 *
 * DID Assignment (Master data → Exotel DIDs) uses these helpers only.
 * It must stay available when AI_CALLING_UI_ENABLED is false — do not gate it
 * through canManageAiCalling / canViewAiCalling.
 */

import { AI_CALLING_UI_ENABLED, canManageAiCalling } from '@/utils/ai-calling-permissions'

/** Existing CASL subject for Admin Settings (do not invent admin_settings). */
export const ADMIN_SETTINGS_SUBJECT = 'admin'

function hasPermission(canFn, action, subject) {
  if (typeof canFn !== 'function') return false
  return !!canFn(action, subject)
}

function tryParseUserData() {
  try {
    return JSON.parse(localStorage.getItem('userData') || '{}') || {}
  } catch (e) {
    return {}
  }
}

/**
 * Org admin — same check as invoice-permissions (`roleId === 1`).
 * Not a new CASL subject.
 */
export function isAdmin(user) {
  const data = user || tryParseUserData()
  const roleId = data.roleId ?? data.roleID ?? data.role_id
  return Number(roleId) === 1
}

/**
 * Create Agent + org AI Calling config (Admin Settings → Agents / AI Calling).
 *
 * Reuses existing abilities — no new CASL subject:
 *   - `isAdmin` (roleId 1)
 *   - `canManageAiCalling` (`manage` + `ai_calling`, or `manage` + `all`)
 *   - `manage` + `admin` (Admin Settings)
 *
 * Recruiters with job-level `read`/`create` on `ai_calling` stay out.
 */
export function canManageAiCallingConfig(canFn) {
  if (!AI_CALLING_UI_ENABLED) return false
  if (isAdmin()) return true
  if (canManageAiCalling(canFn)) return true
  return hasPermission(canFn, 'manage', ADMIN_SETTINGS_SUBJECT)
}

/** Super-admin CASL wildcard — permission, not a JWT role string. */
function hasManageAll(canFn) {
  return hasPermission(canFn, 'manage', 'all')
}

/**
 * Legacy role-as-subject the backend still issues for admin/master users.
 * Kept so existing role users are not locked out. Not required when the user
 * has manage or create on `admin`.
 */
function hasLegacyAdminRole(canFn) {
  return (
    hasPermission(canFn, 'read', 'admin')
    || hasPermission(canFn, 'read', 'master')
  )
}

/** True when CASL grants manage or create on Admin Settings (`admin`). */
export function hasAdminSettingsPermission(canFn) {
  return (
    hasPermission(canFn, 'manage', ADMIN_SETTINGS_SUBJECT)
    || hasPermission(canFn, 'create', ADMIN_SETTINGS_SUBJECT)
    || hasManageAll(canFn)
  )
}

/** Create or manage (or legacy admin/master role). */
export function canCreateMasterData(canFn) {
  return hasAdminSettingsPermission(canFn) || hasLegacyAdminRole(canFn)
}

/** Manage (or delete) — not create-only. */
export function canDeleteMasterData(canFn) {
  return (
    hasPermission(canFn, 'manage', ADMIN_SETTINGS_SUBJECT)
    || hasPermission(canFn, 'delete', ADMIN_SETTINGS_SUBJECT)
    || hasManageAll(canFn)
    || hasLegacyAdminRole(canFn)
  )
}

/** Master data tab + DID Assignment — manage or create. Not role-only. */
export function canManageMasterData(canFn) {
  return canCreateMasterData(canFn)
}

/** Agents tab — Create Agent / voice agent CRUD. Hidden while AI_CALLING_UI_ENABLED is false. */
export function canManageAgentsTab(canFn) {
  return canManageAiCallingConfig(canFn)
}

/** AI Calling tab — org-wide AI calling configuration. Hidden while AI_CALLING_UI_ENABLED is false. */
export function canManageAiCallingTab(canFn) {
  return canManageAiCallingConfig(canFn)
}

export function canAccessAdminTab(canFn, tabKey) {
  switch (tabKey) {
    case 'master-data':
      return canManageMasterData(canFn)
    case 'agents':
      return canManageAgentsTab(canFn)
    case 'ai-calling':
      return canManageAiCallingTab(canFn)
    default:
      return false
  }
}

/** True when the user may open at least one Admin Settings tab. */
export function canAccessAdminSettings(canFn) {
  if (typeof canFn !== 'function') return false
  return (
    canManageMasterData(canFn)
    || canManageAgentsTab(canFn)
    || canManageAiCallingTab(canFn)
  )
}

export function getCurrentEmpId() {
  try {
    const userData = JSON.parse(localStorage.getItem('userData') || '{}')
    return userData?.emp_id || 'system'
  } catch (e) {
    return 'system'
  }
}
